import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { PersonTitleModel } from 'src/app/shared/models/person-title.model';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ContactRepository } from '../../states/contact.repository';
import { ContactModel, PhoneNumbersEntry, PhoneNumbersEntryType } from '../../models/contact.model';
import { WaitingDialogService } from 'src/app/shared/services/waiting-dialog.service';
import { DatePipe, formatDate } from "@angular/common";
import { debounceTime, distinctUntilChanged, filter, Subject, takeWhile, tap } from 'rxjs';
import { PersonGenderModel } from "../../../../shared/models/person-gender.model";
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-choose-contact',
  templateUrl: './choose-contact.component.html',
  styleUrls: ['./choose-contact.component.scss'],
  standalone: false
})
export class ChooseContactComponent implements OnInit, OnDestroy {
  @Input() newContact: ContactModel = {} as ContactModel;
  @Input() isExistingContact: boolean = false;
  @Input() onlyCreateContact: boolean = false;
  @Input() onlyEditContact: boolean = false;
  @Input() editingDisabled: boolean = false;
  @Input() allowEmptyValid: boolean = false;
  /** Neue Kontakte sind als Tierarzt vorbelegt (Tab „Tierärzte“, Schnellanlage). */
  @Input() presetVeterinary: boolean = false;
  alive: boolean = true;
  personSalutationOptions: PersonGenderModel[] = [];
  personTitleOptions: PersonTitleModel[] = [];
  contacts: any[] = [];
  searchTerm: string = '';
  searchTermChanged = new Subject<string>();
  showForm: boolean = false;
  showSearchModal: boolean = false;
  editableFields: { [key: string]: boolean } = {};
  validationError: string = '';
  objectKeys = Object.keys;
  formattedBirthday: string | null = null;

  @Output() newContactUpdated = new EventEmitter<any>();
  @Output() validationStatusChanged = new EventEmitter<boolean>();
  @Output() existingContactUpdated = new EventEmitter<boolean>();
  @Output() existingContactSelected = new EventEmitter<ContactModel>();
  newPhoneNumbers: PhoneNumbersEntry[] = [];
  phoneNumbersEntryType = PhoneNumbersEntryType;

  phoneTypeOptions = [
    { labelKey: 'contacts.mobileType', value: PhoneNumbersEntryType.MOBILE },
    { labelKey: 'contacts.privateType', value: PhoneNumbersEntryType.PRIVATE },
    { labelKey: 'contacts.businessType', value: PhoneNumbersEntryType.BUSINESS },
    { labelKey: 'contacts.customType', value: PhoneNumbersEntryType.CUSTOM },
  ];

  constructor(
    private readonly _helperService: HelperService,
    private readonly _contactRepository: ContactRepository,
    private readonly _waitingDialogService: WaitingDialogService,
    private readonly _ngZone: NgZone,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _datePipe: DatePipe,
    private readonly _translate: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.personSalutationOptions = this._helperService.getSalutationOptions();
    this.personTitleOptions = this._helperService.getTitleOptions();
    if (Object.keys(this.newContact || {}).length === 0) {
      this.validationStatusChanged.emit(this.allowEmptyValid ? true : false);
    } else {
      if (this.newContact.birthday) {
        const birthdayValue = new Date(this.newContact.birthday);
        this.newContact.birthday = birthdayValue;
        this.formattedBirthday = this._datePipe.transform(birthdayValue, 'yyyy-MM-dd');
      }
      this.validateForm();
    }
    if (this.onlyCreateContact) {
      this.createNewContact();
    }

    this.searchTermChanged.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeWhile(() => this.alive),
      tap(term => {
        if (term.length === 0) {
          this.contacts = [];
        }
      }),
      filter(term => term.length >= 2)
    ).subscribe(term => {
      this._waitingDialogService.showWaitingDialog(true);
      this._contactRepository.searchContacts(term).subscribe(
        (results: any[]) => {
          this.contacts = results;
          this._cdr.detectChanges();
          this._ngZone.run(() => {
            this._waitingDialogService.showWaitingDialog(false);
          });
        },
        (error) => {
          console.error('Error fetching contacts', error);
          this.contacts = [];
          this._cdr.detectChanges();
          this._ngZone.run(() => {
            this._waitingDialogService.showWaitingDialog(false);
          });
        }
      );
    });
  }

  openSearchModal(): void {
    this.showSearchModal = true;
  }

  closeSearchModal(): void {
    this.showSearchModal = false;
    this.searchTerm = '';
    this.contacts = [];
  }

  onSearch(): void {
    this.searchTermChanged.next(this.searchTerm);
  }

  takeContact(contact: any): void {
    if (contact.birthday) {
      contact.birthday = new Date(contact.birthday);
    }
    this.newContact = { ...contact };
    this.showForm = true;
    this.isExistingContact = true;
    this.editableFields = {};
    this.validationStatusChanged.emit(true);
    this.existingContactSelected.emit(this.newContact);
    this.closeSearchModal();
    this.validateForm();
  }

  onInputChange(): void {
    if (this.isExistingContact) {
      this.existingContactUpdated.emit(true);
    }

    this.newContactUpdated.emit(this.newContact);
    this.validateForm();
  }

  addPhoneNumber() {
    this.newContact.phoneNumbers ??= [];
    this.newContact.phoneNumbers.push({
      number: null,
      type: PhoneNumbersEntryType.MOBILE,
      label: null,
      isPrimary: false,
    });
    this.onInputChange();
  }

  removePhoneNumber(i: number) {
    this.newContact.phoneNumbers?.splice(i, 1);
    this.onInputChange();
  }

  createNewContact(): void {
    this.newContact = (this.presetVeterinary ? { isVeterinary: true } : {}) as ContactModel;
    this.showForm = true;
    this.isExistingContact = false;
    this.editableFields = {};
    this.existingContactSelected.emit({} as ContactModel);
    this.existingContactUpdated.emit(false);
    this.newContactUpdated.emit(this.newContact);
    this.validationStatusChanged.emit(false);
    this.validationError = '';
  }

  clearContact(): void {
    this.newContact = {} as ContactModel;
    this.showForm = false;
    this.isExistingContact = false;
    this.editableFields = {};
    this.existingContactSelected.emit({} as ContactModel);
    this.existingContactUpdated.emit(false);
    this.newContactUpdated.emit(this.newContact);
    this.validationStatusChanged.emit(this.allowEmptyValid ? true : false);
    this.validateForm();
    this._cdr.detectChanges();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.contacts = [];
    this._cdr.detectChanges();
  }

  validateForm(): void {
    const hasAnyField = !!(this.newContact && (this.newContact.firstName || this.newContact.lastName || this.newContact.companyName));
    const isEmptyObject = Object.keys(this.newContact || {}).length === 0 || this.newContact.id === 0;
    const isValidWhenEmpty = this.allowEmptyValid;
    if (isEmptyObject) {
      this.validationError = isValidWhenEmpty ? '' : 'Bitte geben Sie mindestens Vorname, Nachname oder Firmenname an.';
      this.validationStatusChanged.emit(isValidWhenEmpty);
      return;
    }

    const isValid = hasAnyField;
    this.validationError = isValid || isValidWhenEmpty ? '' : 'Bitte geben Sie mindestens Vorname, Nachname oder Firmenname an.';
    this.validationStatusChanged.emit(isValid);
  }

  protected readonly formatDate = formatDate;
  protected readonly Date = Date;

  ngOnDestroy(): void {
    this.alive = false;
  }
}
